import { VacationAlert, EmployeeVacationData } from '@/types/dashboard'
import {
  getDashboardConfigForEmployee,
  caseInsensitiveEquals,
  type MonumentaEmployee
} from '@/lib/real-data-service'

// Get real vacation data from the real data service - FAIL-FAST: No fallbacks
export async function getRealEmployeeVacationData(currentUser?: MonumentaEmployee | null): Promise<EmployeeVacationData[]> {
  if (!currentUser) {
    throw new Error(
      `🚨 DASHBOARD ERROR: No current user provided.\n` +
      `Please provide a valid user to getRealEmployeeVacationData().`
    );
  }

  const dashboardConfig = await getDashboardConfigForEmployee(currentUser)

  if (!dashboardConfig) {
    throw new Error(
      `🚨 DASHBOARD ERROR: Cannot get dashboard config for user '${currentUser.email}'.\n` +
      `User exists but dashboard configuration failed.\n` +
      `Check user role and department data integrity.`
    );
  }

  // If user is a manager, get their direct reports + themselves
  if (dashboardConfig.isManager && dashboardConfig.directReports.length > 0) {
    const allEmployees = [currentUser, ...dashboardConfig.directReports]
    return allEmployees.map(emp => ({
      id: emp.id.toString(),
      name: emp.name,
      hireDate: emp.admissionDate,
      daysAvailable: emp.vacationBalance
    }))
  }

  // If regular employee, just show their own data
  return [{
    id: currentUser.id.toString(),
    name: currentUser.name,
    hireDate: currentUser.admissionDate,
    daysAvailable: currentUser.vacationBalance
  }]
}

// Calculate vacation alerts for dashboard
export function calculateDashboardVacationAlerts(employees: EmployeeVacationData[]): VacationAlert[] {
  const alerts: VacationAlert[] = []
  const today = new Date()

  employees.forEach(employee => {
    const hireDate = new Date(employee.hireDate)
    const currentBalance = employee.daysAvailable
    
    // Calculate next anniversary date
    const nextAnniversary = new Date(hireDate)
    nextAnniversary.setFullYear(today.getFullYear())
    
    // If anniversary has passed this year, get next year's anniversary
    if (nextAnniversary < today) {
      nextAnniversary.setFullYear(today.getFullYear() + 1)
    }
    
    // Calculate days until anniversary
    const daysUntilAnniversary = Math.ceil(
      (nextAnniversary.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
    )
    
    // Critical Alert: More than 30 days balance (didn't take vacation last period)
    if (currentBalance > 30) {
      alerts.push({
        id: `critical-${employee.id}`,
        employeeId: employee.id,
        employeeName: employee.name,
        type: 'critical_balance',
        severity: 'critical',
        currentBalance,
        message: `${currentBalance} dias acumulados! Risco trabalhista - não gozou férias no período anterior.`,
        actionRequired: true
      })
    }
    // Warning Alert: 20-30 days balance
    else if (currentBalance > 20) {
      alerts.push({
        id: `excessive-${employee.id}`,
        employeeId: employee.id,
        employeeName: employee.name,
        type: 'excessive_balance',
        severity: 'warning',
        currentBalance,
        message: `Saldo elevado: ${currentBalance} dias. Programar férias em breve.`,
        actionRequired: false
      })
    }
    
    // Anniversary approaching with positive balance
    if (daysUntilAnniversary <= 90 && currentBalance > 0) {
      const severity = daysUntilAnniversary <= 30 ? 'critical' : 'warning'
      alerts.push({
        id: `anniversary-${employee.id}`,
        employeeId: employee.id,
        employeeName: employee.name,
        type: 'anniversary_approaching',
        severity,
        daysUntilAnniversary,
        currentBalance,
        message: `Aniversário de admissão em ${daysUntilAnniversary} dias (${currentBalance} dias disponíveis).`,
        actionRequired: daysUntilAnniversary <= 30
      })
    }
  })
  
  // Sort by severity and urgency
  return alerts.sort((a, b) => {
    if (a.severity !== b.severity) {
      return a.severity === 'critical' ? -1 : 1
    }
    if (a.type === 'critical_balance') return -1
    if (b.type === 'critical_balance') return 1
    return (a.daysUntilAnniversary || 0) - (b.daysUntilAnniversary || 0)
  })
}